import { ArrowLeftIcon, CheckIcon, Loader2Icon, SearchIcon, TriangleAlertIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import {
  useHsnSearch,
  useItem,
  useSaveItem,
  type HsnCode,
} from "@/features/items/use-item-list"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useAuth } from "@/features/auth/auth-context"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { ApiError } from "@/lib/api-error"
import { paiseToInputValue, parseRupeesToPaise } from "@/lib/money"
import { basisPointsToInputValue, parsePercentToBasisPoints } from "@/lib/units"
import { routes } from "@/routes/paths"

/*
 * EVERY ITEM IN THESE POPUPS IS A 44px TOUCH TARGET, DELIBERATELY (§11.1).
 *
 * These two controls used to be native <select>s, chosen on purpose: on a phone
 * a native select opens the operating system's own full-height wheel — bigger
 * than anything drawn in the page, already familiar, and reliably hittable
 * without a 48px target. The owner asked for the shadcn Select instead, for
 * visual consistency with the rest of the app, and that is his call — but the
 * property that reasoning was protecting is the TOUCH TARGET, and it does not
 * survive the swap on its own: `SelectItem` ships `py-1` around `text-sm`, a
 * ~28px row, which is materially worse in a workshop than the wheel it
 * replaces.
 *
 * `` is 2.75rem = 44px, spelled in rem rather than `min-h-[44px]`
 * because `styles/preferences.css` scales the root font-size to 18px/20px for
 * the large-text preference precisely so that targets grow with it; a pixel
 * literal would stay stuck at 44 for the person who most needs it bigger. The
 * triggers carry `` too — a trigger is now the only thing to tap to
 * open a list at all.
 *
 * DO NOT "TIDY" THIS AWAY. Deleting it silently withdraws the one condition
 * this migration was granted under.
 */

/*
 * AND THE TRIGGER MUST SAY THE OPTION'S WORDS, NOT ITS VALUE.
 *
 * `Select.Value` does NOT read the chosen item's text. It resolves a label
 * through the root's `items` map (`internals/resolveValueLabel.js`), and with no
 * `items` it falls back to `stringifyAsLabel(value)` — the raw string. A native
 * <select> showed the chosen <option>'s words for free; a `<SelectValue />`
 * left to itself renders "SERVICE" and "BY_WEIGHT" here, which is the database's
 * vocabulary printed on the owner's screen and untranslated in Marathi besides.
 *
 * So each label map is passed to the root AND rendered as the items, which is
 * also the only arrangement in which the trigger and the list cannot drift.
 */

/**
 * Add or edit a saved job (§7, §8.6).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CODE AND THE RATE ARE THE POINT OF THIS SCREEN. A job's name is what the
 * owner recognises; its HSN/SAC code and GST rate are what §4.1 requires on
 * every invoice line and what nobody remembers. Saving them once is the whole
 * value of the rate card.
 *
 * THE CODE IS PICKED FROM THE CA-VERIFIED MASTER, NOT LOOKED UP. §8.6 rules out
 * an HSN lookup API in as many words: the rate is not a property of the code —
 * SAC 9954 spans 1% to 18% depending on the building, the immovable-property
 * relationship and who the customer is. An API returning a flat answer would put
 * an unverifiable rate on a legal tax invoice. The picker offers the small
 * static seed the CA has checked.
 *
 * A CODE THE CA HAS NEVER VERIFIED IS FLAGGED. `lastVerifiedOn` being null means
 * nobody has confirmed the rate, and §8.6's whole posture is that an unchecked
 * rate is a liability rather than a default. Saying so here is cheaper than
 * finding out from a notice.
 *
 * EDITING NEVER CHANGES AN ISSUED BILL. Every line snapshots `gst_rate_bp` at
 * save time (§8.7), so correcting a rate here affects the NEXT bill and leaves
 * every existing one arithmetically intact.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ItemEditorScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = useAuth()
  const { id } = useParams<{ id: string }>()
  const isEdit = id !== undefined

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * MAY THIS PERSON SAVE? — the same mismatch as `CustomerEditorScreen`, one
   * register over, and it arrived the same way.
   *
   * `/items/:id` is mounted behind `item.view` and `/items/new` behind
   * `item.create` (routes/index.tsx), while the server gates `PUT /items/:id` on
   * `item.edit` (routes/items.ts). The ACCOUNTANT holds `item.view` through
   * `VIEW_AND_EXPORT` and does not hold `item.edit`, so every row of the "Items"
   * register — a rail entry offered to her purely because the read capability is
   * on the shared row — was a door into an editor with the HSN, the rate and the
   * GST rate live and Save enabled, ending in a 403 and an audited denial.
   *
   * A CAPABILITY, NOT A ROLE (§2.1). See the longer note in the customer editor
   * for why the screen stays reachable read-only rather than being re-gated on
   * `item.edit`: she needs to be able to look up which HSN a job carries.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const canSave = isEdit ? can("item.edit") : can("item.create")

  const existing = useItem(id)
  const save = useSaveItem(id)

  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [category, setCategory] = useState("SERVICE")
  const [hsn, setHsn] = useState("")
  const [gstRatePercent, setGstRatePercent] = useState("18")
  const [pricingMode, setPricingMode] = useState("LUMPSUM")
  const [unitLabel, setUnitLabel] = useState("NO")
  const [rate, setRate] = useState("")
  const [pickingCode, setPickingCode] = useState(false)
  const [codeUnverified, setCodeUnverified] = useState(false)

  /*
   * Memoised because the Select root writes `items` into its store on every
   * render; a fresh object each time would mark the store dirty for nothing.
   */
  const categoryLabels = useMemo<Record<string, React.ReactNode>>(
    () => ({ SERVICE: t("item.work"), PRODUCT: t("item.material") }),
    [t],
  )
  const pricingLabels = useMemo<Record<string, React.ReactNode>>(
    () => ({
      LUMPSUM: t("line.work"),
      BY_PIECE: t("line.pieces"),
      BY_WEIGHT: t("line.weightKg"),
      BY_LENGTH: t("line.lengthM"),
    }),
    [t],
  )

  useEffect(() => {
    const item = existing.data
    if (item === undefined) return
    setName(item.name)
    setDescription(item.description ?? "")
    setCategory(item.category)
    setHsn(item.hsn)
    setGstRatePercent(basisPointsToInputValue(item.gstRateBp))
    setPricingMode(item.pricingMode)
    setUnitLabel(item.unitLabel)
    setRate(item.ratePaise === 0 ? "" : paiseToInputValue(item.ratePaise))
  }, [existing.data])

  const gstRateBp = parsePercentToBasisPoints(gstRatePercent)
  const ratePaise = rate.trim() === "" ? 0 : parseRupeesToPaise(rate)

  const problems: string[] = []
  if (name.trim() === "") problems.push(t("lbl.required"))
  if (!/^\d{4,8}$/.test(hsn.trim())) problems.push(t("item.codeInvalid"))
  if (gstRateBp === null) problems.push(t("err.notAnAmount"))
  if (ratePaise === null) problems.push(t("err.notAnAmount"))

  const onSave = () => {
    if (problems.length > 0 || gstRateBp === null || ratePaise === null) return

    save.mutate(
      {
        name: name.trim(),
        description: description.trim() === "" ? undefined : description.trim(),
        category,
        hsn: hsn.trim(),
        gstRateBp,
        pricingMode,
        unitLabel: unitLabel.trim() === "" ? "NO" : unitLabel.trim(),
        ratePaise,
      },
      {
        onSuccess: (item) => {
          toast.success(t("item.saved", { name: item.name }))
          void navigate(routes.items, { replace: true })
        },
      },
    )
  }

  if (isEdit && existing.isPending) return <EditorSkeleton />

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Link
        to={routes.items}
        className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
      >
        <ArrowLeftIcon className="size-4" />
        {t("nav.items")}
      </Link>

      <header>
        {/* "Change the name, the rate or the code" over a form nobody can
          * submit is what makes a 403 read as a broken app. */}
        <h1 className="text-2xl font-semibold tracking-tight">
          {!canSave ? t("item.viewTitle") : isEdit ? t("item.editTitle") : t("item.addTitle")}
        </h1>
        <p className="text-muted-foreground text-sm">
          {!canSave
            ? t("item.viewSubtitle")
            : isEdit
              ? t("item.editSubtitle")
              : t("item.addSubtitle")}
        </p>
      </header>

      {save.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotSave")}</AlertTitle>
          <AlertDescription>
            {save.error instanceof ApiError ? save.error.message : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      ) : null}

      {/*
        * ONE `fieldset`, for the reason spelled out in `CustomerEditorScreen`: a
        * disabled fieldset disables every form control inside it as plain HTML,
        * so the HSN picker's button, the rate box and anything added here later
        * are all covered without a per-field list to keep in step.
        * `display: contents` keeps the parent's `flex-col gap-4` laying the
        * cards out exactly as before.
        */}
      <fieldset disabled={!canSave} className="contents">
      <Card>
        <CardHeader>
          <CardTitle>{t("item.aboutSection")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="item-name">{t("lbl.jobName")}</Label>
            <Input
              id="item-name"
              value={name}
              onChange={(event) => {
                setName(event.target.value)
              }}
            />
            <p className="text-muted-foreground text-xs">{t("item.nameHelp")}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="item-description">{t("lbl.longDescription")}</Label>
            <Textarea
              id="item-description"
              rows={2}
              value={description}
              onChange={(event) => {
                setDescription(event.target.value)
              }}
            />
            <p className="text-muted-foreground text-xs">{t("item.descriptionHelp")}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="item-category">{t("item.kindLabel")}</Label>
            <Select
              items={categoryLabels}
              value={category}
              onValueChange={(value) => {
                /*
                 * The RAW ENUM STRING, never a label — the help line below
                 * branches on `category === "SERVICE"`, and the value is what
                 * `POST /items` stores. `onValueChange` is typed to allow null
                 * (there is no clearing item here, but the type admits it), and
                 * a null would blank a required field, so it is ignored.
                 */
                if (value == null) return
                setCategory(String(value))
              }}
            >
              {/* The id belongs on the TRIGGER — that is the <button
                * role="combobox"> the <Label htmlFor="item-category"> names. */}
              <SelectTrigger id="item-category" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categoryLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              {category === "SERVICE" ? t("item.workHelp") : t("item.materialHelp")}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("item.priceSection")}</CardTitle>
          <CardDescription>{t("item.codeHelp")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>HSN/SAC</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="tabular-nums">
                {hsn === "" ? "—" : t("item.codeChip", { code: hsn, rate: gstRatePercent })}
              </Badge>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setPickingCode(true)
                }}
              >
                <SearchIcon />
                {t("item.codeDialogTitle")}
              </Button>
            </div>

            {/*
              * §8.6 — a rate nobody has checked is a liability, not a default.
              * Saying so at the moment it is chosen is cheaper than finding out
              * from a notice a year later.
              */}
            {codeUnverified ? (
              <Alert className="mt-1">
                <TriangleAlertIcon />
                <AlertDescription>{t("taxCodes.neverVerified")}</AlertDescription>
              </Alert>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="item-hsn">{t("line.taxCodeField")}</Label>
            <Input
              id="item-hsn"
              value={hsn}
              inputMode="numeric"
              maxLength={8}
              className="tabular-nums"
              onChange={(event) => {
                setHsn(event.target.value)
                setCodeUnverified(false)
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="item-rate-pct">{t("line.gstRate")}</Label>
            <Input
              id="item-rate-pct"
              value={gstRatePercent}
              inputMode="decimal"
              className="tabular-nums"
              onChange={(event) => {
                setGstRatePercent(event.target.value)
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="item-rate">{t("lbl.rate")}</Label>
            <Input
              id="item-rate"
              value={rate}
              inputMode="decimal"
              placeholder="0.00"
              className="tabular-nums"
              onChange={(event) => {
                setRate(event.target.value)
              }}
            />
            <p className="text-muted-foreground text-xs">{t("item.rateHelp")}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="item-unit">{t("lbl.unit")}</Label>
            <Input
              id="item-unit"
              value={unitLabel}
              maxLength={10}
              onChange={(event) => {
                setUnitLabel(event.target.value)
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="item-pricing">{t("line.workKind")}</Label>
            <Select
              items={pricingLabels}
              value={pricingMode}
              onValueChange={(value) => {
                // Raw enum string again; null would blank a required field.
                if (value == null) return
                setPricingMode(String(value))
              }}
            >
              <SelectTrigger id="item-pricing" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(pricingLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isEdit ? <InListCard id={id} /> : null}
      </fieldset>

      {/* A validation nag on a form nobody can submit is noise. */}
      {canSave && problems.length > 0 ? (
        <p className="text-muted-foreground text-sm">{t("err.fixHighlighted")}</p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {/* Absent, not disabled — see the customer editor. */}
        {canSave ? (
          <Button disabled={problems.length > 0 || save.isPending} onClick={onSave}>
            {save.isPending ? <Loader2Icon className="animate-spin" /> : <CheckIcon />}
            {t("btn.save")}
          </Button>
        ) : null}
        <Button variant="outline" nativeButton={false} render={<Link to={routes.items} />}>
          {canSave ? t("btn.cancel") : t("btn.back")}
        </Button>
      </div>

      <HsnPicker
        open={pickingCode}
        onOpenChange={setPickingCode}
        onPick={(code) => {
          setHsn(code.code)
          // The MASTER's rate is a starting point, not a verdict — §8.6. It is
          // filled in and remains editable.
          setGstRatePercent(basisPointsToInputValue(code.defaultGstRateBp))
          setCodeUnverified(code.lastVerifiedOn === null)
          setPickingCode(false)
        }}
      />
    </div>
  )
}

/** The CA-verified code list (§8.6). A small static seed, never a lookup API. */
function HsnPicker({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (code: HsnCode) => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const [q, setQ] = useState("")
  const query = useHsnSearch(q)
  const codes = query.data?.codes ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-3 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("item.codeDialogTitle")}</DialogTitle>
          <DialogDescription>{t("item.codeDialogHelp")}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <SearchIcon
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
          />
          <Input
            type="search"
            className="pl-9"
            aria-label={t("item.codeSearchLabel")}
            placeholder={t("item.codeSearchPlaceholder")}
            value={q}
            onChange={(event) => {
              setQ(event.target.value)
            }}
            autoFocus
          />
        </div>

        <div className="-mx-6 flex-1 overflow-y-auto border-t">
          {query.isPending ? (
            <div className="flex flex-col gap-3 p-6">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-40" />
            </div>
          ) : codes.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              {t("item.codeNotAllowed")}
            </p>
          ) : (
            <ul className="flex flex-col">
              {codes.map((code) => (
                <li key={code.id} className="border-b last:border-b-0">
                  <button
                    type="button"
                    onClick={() => {
                      onPick(code)
                    }}
                    className="hover:bg-accent/50 flex min-h-14 w-full items-center gap-3 px-6 py-3 text-left transition-colors"
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="font-medium tabular-nums">{code.code}</span>
                      <span className="text-muted-foreground truncate text-xs">
                        {code.description}
                      </span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-1">
                      <Badge variant="outline" className="tabular-nums">
                        {t("item.percent", {
                          rate: basisPointsToInputValue(code.defaultGstRateBp),
                        })}
                      </Badge>
                      {/* §8.6 — an unchecked rate is flagged where it is chosen. */}
                      {code.lastVerifiedOn === null ? (
                        <span className="text-muted-foreground text-[0.65rem]">
                          {t("taxCodes.neverVerified")}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Keep or retire. Saved on its own, like the customer archive switch. */
function InListCard({ id }: { id: string }): React.JSX.Element {
  const { t } = useTranslation()
  const existing = useItem(id)
  const save = useSaveItem(id)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("item.inList")}</CardTitle>
        <CardDescription>{t("item.inListHelp")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <Switch
            id="item-active"
            checked={existing.data?.isActive ?? true}
            disabled={save.isPending || existing.isPending}
            onCheckedChange={(checked) => {
              save.mutate({ isActive: checked })
            }}
          />
          <Label htmlFor="item-active">{t("item.inList")}</Label>
        </div>
      </CardContent>
    </Card>
  )
}

function EditorSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-56 w-full" />
    </div>
  )
}
